import React, { useMemo, useState, useEffect, useRef } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import Header from '../../../components/Headers/Header'
import { api } from '../../../utils/index'
import { notify, prefix } from '../../../utils/index_tagging_tool'
import {
  runSavedDimensionAiRegexPreview,
  saveSavedDimensionAiRegexPrompt,
} from '../../api_methods'

const MODEL_OPTIONS = [
  'gpt-5.4-nano',
  'gpt-5.4-mini',
  'gpt-5-nano',
  'gpt-4o-mini',
  'gpt-5.1-codex',
  'gemini-3-flash-preview',
  'gemini-3.1-flash-lite-preview',
]

const DEFAULT_SYSTEM_PROMPT =
  'You generate keyword-tagging regex for SEO keyword classification. ' +
  'Return valid JSON only with keys: pos_regex, neg_regex. ' +
  'Output must be parseable by json.loads and regex must compile in Python re (IGNORECASE). ' +
  'Avoid using inline flags like (?i). ' +
  'Do not include markdown, comments, or additional keys. ' +
  'Rules: ' +
  '1) pos_regex must be specific and use boundaries for lexical terms (for example \\\\b...\\\\b). ' +
  '2) Prefer explicit alternations over greedy catch-all patterns; avoid broad .* unless needed for word-order swaps. ' +
  '3) Add neg_regex whenever the tag can overlap with adjacent intents (product vs machine vs accessory vs beverage contexts). ' +
  '4) For multi-word concepts, support order variants using non-capturing groups. ' +
  '5) If the target tag is ambiguous, keep pos_regex narrow and protect precision with neg_regex. ' +
  '6) Use the requested language and include common spelling variants/diacritics.'

const sortMetrics = (metricsByModel, sortField, sortDirection) => {
  const rows = Object.entries(metricsByModel || {}).map(([model, metrics]) => ({
    model,
    ...metrics,
  }))

  rows.sort((a, b) => {
    const left = a[sortField] || 0
    const right = b[sortField] || 0
    if (left === right) return a.model.localeCompare(b.model)
    return sortDirection === 'desc' ? right - left : left - right
  })

  return rows
}

const METRIC_TOOLTIPS = {
  exact_match_rate: 'Percentuale di keyword in cui il set di tag del modello coincide esattamente con il ground truth.',
  micro_precision: 'Tra tutti i tag previsti dal modello, quanti sono corretti (valore alto = meno falsi positivi).',
  micro_recall: 'Tra tutti i tag attesi, quanti sono stati trovati dal modello (valore alto = meno falsi negativi).',
  micro_f1: 'Media armonica tra precisione e recall, bilancia accuratezza e copertura.',
  avg_jaccard: 'Sovrapposizione media tra set di tag previsti e ground truth per keyword (intersezione / unione).',
}

const MetricHeader = ({ label, tooltipId, tooltipText }) => (
  <span className="d-inline-flex align-items-center">
    <span>{label}</span>
    <OverlayTrigger
      trigger={['hover', 'focus', 'click']}
      placement="top"
      overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ml-1 align-baseline"
        aria-label={`Info ${label}`}
        title={tooltipText}
        style={{ lineHeight: 1, textDecoration: 'none' }}
      >
        <i className="fa fa-info-circle text-info" />
      </button>
    </OverlayTrigger>
  </span>
)

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

export default function AIRegexBetaPage() {
  const [savedDimensions, setSavedDimensions] = useState([])
  const [loadingDimensions, setLoadingDimensions] = useState(true)
  const [languages, setLanguages] = useState([])
  const [selectedLanguage, setSelectedLanguage] = useState('it')
  const [selectedDimensionId, setSelectedDimensionId] = useState('')
  const [prompt, setPrompt] = useState('')
  const [selectedModels, setSelectedModels] = useState([])
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT)
  const [file, setFile] = useState(null)
  const [running, setRunning] = useState(false)
  const [savingPrompt, setSavingPrompt] = useState(false)
  const [rows, setRows] = useState([])
  const [groundTruthColumn, setGroundTruthColumn] = useState('')
  const [modelColumns, setModelColumns] = useState({})
  const [metricsByModel, setMetricsByModel] = useState({})
  const [warningsByModel, setWarningsByModel] = useState({})
  const [groundTruthWarnings, setGroundTruthWarnings] = useState([])
  const [regexTags, setRegexTags] = useState([])
  const [regexesByDimension, setRegexesByDimension] = useState({})
  const [previewRan, setPreviewRan] = useState(false)
  const [sortField, setSortField] = useState('micro_f1')
  const [sortDirection, setSortDirection] = useState('desc')
  const [activeResultTab, setActiveResultTab] = useState('results')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [showCombinedRegex, setShowCombinedRegex] = useState(false)
  const [regexDetail, setRegexDetail] = useState(null)
  const [showOnlyRegexDifferences, setShowOnlyRegexDifferences] = useState(false)
  const [regexCompareDetail, setRegexCompareDetail] = useState(null)
  const resultsRef = useRef(null)
  const warnedHeavyDimensionRef = useRef(null)
  const warnedCodexCostRef = useRef(false)

  useEffect(() => {
    setLoadingDimensions(true)
    api
      .get(`${prefix}saved-dimensions/`)
      .then((res) => {
        setSavedDimensions(res.data || [])
      })
      .catch(() => {
        notify('Errore nel caricamento delle dimensioni salvate', 'error')
      })
      .finally(() => {
        setLoadingDimensions(false)
      })
  }, [])

  useEffect(() => {
    api
      .get(`${prefix}languages/`)
      .then((res) => {
        const languageOptions = res.data || []
        setLanguages(languageOptions)
        const codes = languageOptions.map((language) => language.code)
        if (codes.length > 0 && !codes.includes(selectedLanguage)) {
          setSelectedLanguage(codes[0])
        }
      })
      .catch(() => {
        notify('Errore nel caricamento delle lingue', 'error')
      })
  }, [])

  const selectedDimension = useMemo(
    () => savedDimensions.find((dimension) => String(dimension.id) === String(selectedDimensionId)),
    [savedDimensions, selectedDimensionId]
  )

  useEffect(() => {
    if (selectedDimension) {
      setPrompt(selectedDimension.ai_regex_dimension_description || '')
    } else {
      setPrompt('')
    }
  }, [selectedDimension])

  useEffect(() => {
    if (!selectedDimension) {
      warnedHeavyDimensionRef.current = null
      return
    }

    const ruleCount =
      Number(selectedDimension.manual_rules_count) ||
      Number(selectedDimension.rules_count) ||
      Number(selectedDimension.rulesCount) ||
      0

    if (ruleCount > 500 && warnedHeavyDimensionRef.current !== selectedDimension.id) {
      notify(
        `Attenzione: "${selectedDimension.display_name}" ha ${ruleCount} regole. La preview AI regex può richiedere più tempo e i costi del test potrebbero essere più alti.`,
        'warning'
      )
      warnedHeavyDimensionRef.current = selectedDimension.id
    }
  }, [selectedDimension])

  useEffect(() => {
    const codexSelected = selectedModels.includes('gpt-5.1-codex')
    if (codexSelected && !warnedCodexCostRef.current) {
      notify(
        'Attenzione: selezionando gpt-5.1-codex i costi del test potrebbero essere più alti rispetto ai modelli più leggeri.',
        'warning'
      )
      warnedCodexCostRef.current = true
    }
    if (!codexSelected) {
      warnedCodexCostRef.current = false
    }
  }, [selectedModels])

  const metricsRows = useMemo(
    () => sortMetrics(metricsByModel, sortField, sortDirection),
    [metricsByModel, sortField, sortDirection]
  )

  const canRun = Boolean(selectedDimensionId && file && selectedModels.length > 0 && !running && !loadingDimensions)

  const onModelsChange = (event) => {
    const values = Array.from(event.target.selectedOptions || []).map((option) => option.value)
    setSelectedModels(values)
  }

  const onSavePrompt = async () => {
    if (!selectedDimensionId) {
      notify('Seleziona prima una dimensione salvata', 'warning')
      return
    }

    setSavingPrompt(true)
    try {
      await saveSavedDimensionAiRegexPrompt(selectedDimensionId, prompt)
      notify('Prompt salvato', 'success')
      setSavedDimensions((current) =>
        current.map((dimension) =>
          String(dimension.id) === String(selectedDimensionId)
            ? { ...dimension, ai_regex_dimension_description: prompt }
            : dimension
        )
      )
    } catch (error) {
      notify(error?.response?.data?.error || 'Errore durante il salvataggio del prompt', 'error')
    } finally {
      setSavingPrompt(false)
    }
  }

  const onRun = async (event) => {
    event.preventDefault()
    if (!canRun) {
      if (selectedModels.length === 0) {
        notify('Seleziona almeno un modello', 'warning')
      }
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    selectedModels.forEach((model) => formData.append('models', model))
    formData.append('dimension_description', prompt)
    formData.append('system_prompt', systemPrompt)
    formData.append('language', selectedLanguage)

    setRunning(true)
    setCurrentPage(1)
    try {
      const response = await runSavedDimensionAiRegexPreview(selectedDimensionId, formData)
      // Some environments return raw payload, others return an Axios-like { data } object.
      const payload = response?.data && !response?.rows ? response.data : response
      setRows(Array.isArray(payload?.rows) ? payload.rows : [])
      setGroundTruthColumn(payload?.ground_truth_column || '')
      setModelColumns(payload?.model_columns || {})
      setMetricsByModel(payload?.metrics_by_model || {})
      setWarningsByModel(payload?.warnings_by_model || {})
      setGroundTruthWarnings(payload?.ground_truth_warnings || [])
      setRegexTags(Array.isArray(payload?.regex_tags) ? payload.regex_tags : [])
      setRegexesByDimension(payload?.regexes_by_dimension || {})
      setActiveResultTab('results')
      setPreviewRan(true)
    } catch (error) {
      setPreviewRan(false)
      notify(error?.response?.data?.error || 'Errore durante la preview AI regex', 'error')
    } finally {
      setRunning(false)
    }
  }

  const previewModels = useMemo(() => {
    const responseModels = Object.keys(modelColumns || {})
    if (responseModels.length > 0) {
      return responseModels
    }
    return selectedModels
  }, [modelColumns, selectedModels])

  const totalRows = rows.length
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage))
  const safeCurrentPage = Math.min(currentPage, totalPages)
  const pageStart = (safeCurrentPage - 1) * rowsPerPage
  const pageEnd = pageStart + rowsPerPage
  const paginatedRows = rows.slice(pageStart, pageEnd)
  const regexDimensionColumns = useMemo(
    () => [groundTruthColumn, ...previewModels.map((model) => modelColumns[model])].filter(Boolean),
    [groundTruthColumn, previewModels, modelColumns]
  )
  const regexModelColumns = useMemo(
    () => previewModels.map((model) => modelColumns[model]).filter(Boolean),
    [previewModels, modelColumns]
  )

  useEffect(() => {
    if (previewRan && resultsRef.current && typeof resultsRef.current.scrollIntoView === 'function') {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [previewRan, rows.length])

  const exportPreviewToExcel = () => {
    if (!rows.length) {
      notify('Nessun risultato da esportare', 'warning')
      return
    }

    const headerCells = [
      'Keyword',
      groundTruthColumn || 'Verita di riferimento',
      ...previewModels.map((model) => modelColumns[model] || `${groundTruthColumn || 'Verita di riferimento'}__${model}`),
    ]

    const previewRows = rows.map((row) => {
      const baseCells = [row.Keyword || row.keyword || '', row[groundTruthColumn] || '']
      const modelCells = previewModels.map((model) => {
        const key = modelColumns[model] || `${groundTruthColumn || 'Verita di riferimento'}__${model}`
        return row[key] || ''
      })
      return [...baseCells, ...modelCells]
    })

    const evaluationHeader = [
      'Modello',
      'Corrispondenza esatta',
      'Micro precisione',
      'Micro recall',
      'Micro F1',
      'Jaccard medio',
    ]
    const evaluationRows = metricsRows.map((metric) => [
      metric.model || '',
      metric.exact_match_rate ?? '',
      metric.micro_precision ?? '',
      metric.micro_recall ?? '',
      metric.micro_f1 ?? '',
      metric.avg_jaccard ?? '',
    ])

    const regexColumns = [groundTruthColumn, ...previewModels.map((model) => modelColumns[model])].filter(Boolean)
    const regexHeaders = [
      'Tag',
      ...regexColumns.flatMap((column) => [`${column} - POS`, `${column} - NEG`, `${column} - COMBINED`]),
    ]
    const regexRows = regexTags.map((tag) => {
      const cells = [tag]
      regexColumns.forEach((column) => {
        const cell = regexesByDimension?.[column]?.[tag] || {}
        cells.push(cell.pos_regex || '', cell.neg_regex || '', cell.combined_regex || '')
      })
      return cells
    })

    const compareHeaders = ['Tag', groundTruthColumn || 'Ground truth', ...regexModelColumns]
    const compareRows = regexTags.map((tag) => {
      const groundTruthCell = regexesByDimension?.[groundTruthColumn]?.[tag] || {}
      const groundTruthHasRegex = Boolean(
        groundTruthCell?.pos_regex || groundTruthCell?.neg_regex || groundTruthCell?.combined_regex
      )
      const row = [tag, groundTruthHasRegex ? 'Ground truth' : 'Vuota']
      regexModelColumns.forEach((column) => {
        const modelCell = regexesByDimension?.[column]?.[tag] || {}
        const status = getComparisonStatus(groundTruthCell, modelCell)
        row.push(status.text)
      })
      return row
    })

    const renderCell = (value) => {
      const isNumber = typeof value === 'number' && Number.isFinite(value)
      const type = isNumber ? 'Number' : 'String'
      return `<Cell><Data ss:Type="${type}">${escapeXml(value)}</Data></Cell>`
    }

    const renderWorksheet = (name, headers, dataRows) => `
      <Worksheet ss:Name="${escapeXml(name)}">
        <Table>
          <Row>${headers.map((header) => renderCell(header)).join('')}</Row>
          ${dataRows.map((rowCells) => `<Row>${rowCells.map((cell) => renderCell(cell)).join('')}</Row>`).join('')}
        </Table>
      </Worksheet>
    `

    const workbookXml = `<?xml version="1.0" encoding="UTF-8"?>
      <Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
        xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
        xmlns:html="http://www.w3.org/TR/REC-html40">
        ${renderWorksheet('Risultati preview', headerCells, previewRows)}
        ${renderWorksheet('Valutazione modelli', evaluationHeader, evaluationRows)}
        ${renderWorksheet('Regex per tag', regexHeaders, regexRows)}
        ${renderWorksheet('Confronto regex', compareHeaders, compareRows)}
      </Workbook>`

    const blob = new Blob([`\ufeff${workbookXml}`], { type: 'application/vnd.ms-excel;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    link.href = url
    link.download = `ai_regex_preview_${timestamp}.xls`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const getRegexCellStatus = (cell) => {
    const hasPos = Boolean(cell?.pos_regex)
    const hasNeg = Boolean(cell?.neg_regex)
    if (!hasPos && !hasNeg) return { text: 'Vuota', className: 'badge-light' }
    if (hasPos && hasNeg) return { text: 'POS + NEG', className: 'badge-success' }
    if (hasPos) return { text: 'Solo POS', className: 'badge-info' }
    return { text: 'Solo NEG', className: 'badge-warning' }
  }

  const normalizeRegexValue = (value) => String(value || '').trim().replace(/\s+/g, ' ')

  const areRegexCellsEquivalent = (leftCell, rightCell) => {
    const leftPos = normalizeRegexValue(leftCell?.pos_regex)
    const rightPos = normalizeRegexValue(rightCell?.pos_regex)
    const leftNeg = normalizeRegexValue(leftCell?.neg_regex)
    const rightNeg = normalizeRegexValue(rightCell?.neg_regex)
    return leftPos === rightPos && leftNeg === rightNeg
  }

  const getComparisonStatus = (groundTruthCell, modelCell) => {
    const hasModelRegex = Boolean(modelCell?.pos_regex || modelCell?.neg_regex || modelCell?.combined_regex)
    const hasGroundTruthRegex = Boolean(
      groundTruthCell?.pos_regex || groundTruthCell?.neg_regex || groundTruthCell?.combined_regex
    )
    if (!hasModelRegex) return { text: 'Vuota', className: 'badge-light' }
    if (!hasGroundTruthRegex) return { text: 'Solo modello', className: 'badge-secondary' }
    if (areRegexCellsEquivalent(groundTruthCell, modelCell)) {
      return { text: '= GT', className: 'badge-success' }
    }
    return { text: '≠ GT', className: 'badge-warning' }
  }

  const tagHasDifferences = (tag) => {
    const groundTruthCell = regexesByDimension?.[groundTruthColumn]?.[tag] || {}
    return regexModelColumns.some((column) => {
      const modelCell = regexesByDimension?.[column]?.[tag] || {}
      const status = getComparisonStatus(groundTruthCell, modelCell)
      return status.text !== '= GT'
    })
  }

  const regexCompareTags = useMemo(() => {
    if (!showOnlyRegexDifferences) return regexTags
    return regexTags.filter((tag) => tagHasDifferences(tag))
  }, [regexTags, showOnlyRegexDifferences, regexesByDimension, groundTruthColumn, regexModelColumns])

  const copyRegexText = async (text, label) => {
    const content = text || ''
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(content)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = content
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'absolute'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      notify(`${label} copiato`, 'success')
    } catch (error) {
      notify(`Impossibile copiare ${label}`, 'warning')
    }
  }

  return (
    <>
      <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
      <div className="row" style={{ marginBottom: '100px' }}>
        <div className="col-12">
          <div className="card bg-secondary shadow">
            <div className="card-header bg-white border-0">
              <h1 className="mb-0">AI Regex Beta Evaluation</h1>
            </div>
            <div className="card-body">
              <form onSubmit={onRun}>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label htmlFor="saved-dimension-select">Dimensione salvata</label>
                      <select
                        id="saved-dimension-select"
                        className="form-control"
                        value={selectedDimensionId}
                        disabled={loadingDimensions}
                        onChange={(event) => setSelectedDimensionId(event.target.value)}
                      >
                        <option value="">
                          {loadingDimensions ? 'Caricamento dimensioni...' : 'Seleziona una dimensione'}
                        </option>
                        {savedDimensions.map((dimension) => (
                          <option key={dimension.id} value={dimension.id}>
                            {dimension.display_name}
                          </option>
                        ))}
                      </select>
                      {loadingDimensions && (
                        <small className="form-text text-muted">Caricamento dimensioni salvate...</small>
                      )}
                      {!loadingDimensions && selectedDimensionId && (
                        <small className="form-text">
                          <a
                            href={`/reporting/saved-dimensions/${selectedDimensionId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Apri dimensione selezionata
                          </a>
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label htmlFor="keywords-file">File keyword</label>
                      <input
                        id="keywords-file"
                        className="form-control"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(event) => setFile(event.target.files?.[0] || null)}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="language-select">Lingua regex</label>
                  <select
                    id="language-select"
                    className="form-control"
                    value={selectedLanguage}
                    onChange={(event) => setSelectedLanguage(event.target.value)}
                  >
                    {languages.length === 0 ? (
                      <option value="it">it</option>
                    ) : (
                      languages.map((language) => (
                        <option key={language.code} value={language.code}>
                          {language.code} - {language.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>Modelli (selezione multipla)</label>
                  <select
                    aria-label="Modelli (selezione multipla)"
                    className="form-control"
                    multiple
                    size={Math.min(MODEL_OPTIONS.length, 6)}
                    value={selectedModels}
                    onChange={onModelsChange}
                  >
                    {MODEL_OPTIONS.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">Tieni premuto Ctrl/Cmd per selezionare piu modelli.</small>
                </div>

                <div className="form-group">
                  <label htmlFor="prompt-editor">Descrizione dimensione</label>
                  <textarea
                    id="prompt-editor"
                    className="form-control"
                    rows={6}
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Descrivi la dimensione per aiutare il modello a generare regex precise"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="system-prompt-editor">System prompt (opzionale)</label>
                  <textarea
                    id="system-prompt-editor"
                    className="form-control"
                    rows={5}
                    value={systemPrompt}
                    onChange={(event) => setSystemPrompt(event.target.value)}
                    placeholder="Lascia vuoto per usare il system prompt di default del backend"
                  />
                </div>

                <div className="d-flex">
                  <button
                    type="button"
                    className="btn btn-info"
                    onClick={onSavePrompt}
                    disabled={savingPrompt || !selectedDimensionId}
                  >
                    {savingPrompt ? 'Salvataggio...' : 'Salva descrizione'}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success ml-2"
                    disabled={!canRun}
                    data-testid="run-beta-button"
                  >
                    {running ? 'Esecuzione...' : 'Avvia valutazione'}
                  </button>
                </div>
                <small className="form-text text-muted mt-2">
                  Dopo l'esecuzione scorri alla sezione risultati: la tabella preview e paginata.
                </small>
                <small className="form-text text-warning mt-1">
                  Le regex generate in questa preview non vengono salvate.
                </small>
              </form>
            </div>
          </div>
        </div>
      </div>

      {previewRan && (
      <div className="row" ref={resultsRef}>
          <div className="col-12">
            <div className="card shadow">
              <div className="card-header bg-white">
                <h3 className="mb-0">Risultati</h3>
                <ul className="nav nav-tabs card-header-tabs mt-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeResultTab === 'evaluation' ? 'active' : ''}`}
                      onClick={() => setActiveResultTab('evaluation')}
                    >
                      Valutazione modelli
                      <span className="badge badge-light ml-2">{metricsRows.length}</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeResultTab === 'results' ? 'active' : ''}`}
                      onClick={() => setActiveResultTab('results')}
                    >
                      Risultati preview
                      <span className="badge badge-light ml-2">{totalRows}</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeResultTab === 'warnings' ? 'active' : ''}`}
                      onClick={() => setActiveResultTab('warnings')}
                    >
                      Avvisi
                      <span className="badge badge-light ml-2">
                        {Object.values(warningsByModel || {}).reduce((sum, list) => sum + (list?.length || 0), 0) +
                          groundTruthWarnings.length}
                      </span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeResultTab === 'regex' ? 'active' : ''}`}
                      onClick={() => setActiveResultTab('regex')}
                    >
                      Regex generate
                      <span className="badge badge-light ml-2">{regexTags.length}</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeResultTab === 'regex-compare' ? 'active' : ''}`}
                      onClick={() => setActiveResultTab('regex-compare')}
                    >
                      Confronto regex
                      <span className="badge badge-light ml-2">{regexCompareTags.length}</span>
                    </button>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                {activeResultTab === 'evaluation' && (
                  <>
                    <div className="d-flex justify-content-end mb-3">
                      <select
                        className="form-control"
                        style={{ width: '220px', display: 'inline-block' }}
                        value={`${sortField}:${sortDirection}`}
                        onChange={(event) => {
                          const [field, direction] = event.target.value.split(':')
                          setSortField(field)
                          setSortDirection(direction)
                        }}
                      >
                        <option value="micro_f1:desc">Micro F1 (desc)</option>
                        <option value="exact_match_rate:desc">Corrispondenza esatta (desc)</option>
                        <option value="micro_precision:desc">Micro precisione (desc)</option>
                        <option value="micro_recall:desc">Micro recall (desc)</option>
                        <option value="avg_jaccard:desc">Jaccard medio (desc)</option>
                      </select>
                    </div>
                    {metricsRows.length === 0 ? (
                      <p className="text-muted mb-0">Nessuna metrica disponibile per la preview corrente.</p>
                    ) : (
                      <table className="table table-responsive">
                        <thead>
                          <tr>
                            <th>Modello</th>
                            <th>
                              <MetricHeader
                                label="Corrispondenza esatta"
                                tooltipId="metric-exact-match"
                                tooltipText={METRIC_TOOLTIPS.exact_match_rate}
                              />
                            </th>
                            <th>
                              <MetricHeader
                                label="Micro precisione"
                                tooltipId="metric-micro-precision"
                                tooltipText={METRIC_TOOLTIPS.micro_precision}
                              />
                            </th>
                            <th>
                              <MetricHeader
                                label="Micro recall"
                                tooltipId="metric-micro-recall"
                                tooltipText={METRIC_TOOLTIPS.micro_recall}
                              />
                            </th>
                            <th>
                              <MetricHeader
                                label="Micro F1"
                                tooltipId="metric-micro-f1"
                                tooltipText={METRIC_TOOLTIPS.micro_f1}
                              />
                            </th>
                            <th>
                              <MetricHeader
                                label="Jaccard medio"
                                tooltipId="metric-avg-jaccard"
                                tooltipText={METRIC_TOOLTIPS.avg_jaccard}
                              />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {metricsRows.map((metric) => (
                            <tr key={metric.model}>
                              <td>{metric.model}</td>
                              <td>{Number(metric.exact_match_rate || 0).toFixed(4)}</td>
                              <td>{Number(metric.micro_precision || 0).toFixed(4)}</td>
                              <td>{Number(metric.micro_recall || 0).toFixed(4)}</td>
                              <td>{Number(metric.micro_f1 || 0).toFixed(4)}</td>
                              <td>{Number(metric.avg_jaccard || 0).toFixed(4)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}

                {activeResultTab === 'results' && (
                  <>
                    {previewModels.length > 0 && (
                      <div className="mb-2">
                        <small className="text-muted mr-2">Modelli confrontati:</small>
                        {previewModels.map((model) => (
                          <span key={`preview-model-${model}`} className="badge badge-light mr-1">
                            {model}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <label htmlFor="rows-per-page" className="mb-0 mr-2 text-muted">
                          Righe per pagina
                        </label>
                        <select
                          id="rows-per-page"
                          className="form-control"
                          style={{ width: '110px' }}
                          value={rowsPerPage}
                          onChange={(event) => {
                            setRowsPerPage(Number(event.target.value))
                            setCurrentPage(1)
                          }}
                        >
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={250}>250</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={exportPreviewToExcel}
                        disabled={rows.length === 0}
                      >
                        Esporta Excel
                      </button>
                    </div>
                    <div
                      style={{
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        border: '1px solid #e9ecef',
                        borderRadius: '0.375rem',
                      }}
                    >
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>Keyword</th>
                            <th style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                              {groundTruthColumn || 'Verita di riferimento'}
                            </th>
                            {previewModels.map((model) => (
                              <th
                                key={model}
                                style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}
                              >
                                {modelColumns[model] || `${groundTruthColumn || 'Verita di riferimento'}__${model}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 && (
                            <tr>
                              <td colSpan={2 + previewModels.length}>
                                Nessun risultato disponibile per la preview corrente.
                              </td>
                            </tr>
                          )}
                          {paginatedRows.map((row, index) => (
                            <tr key={`${row.Keyword || row.keyword || 'keyword'}-${index}`}>
                              <td>{row.Keyword || row.keyword || ''}</td>
                              <td>{row[groundTruthColumn] || ''}</td>
                              {previewModels.map((model) => {
                                const key = modelColumns[model] || `${groundTruthColumn || 'Verita di riferimento'}__${model}`
                                return <td key={`${key}-${index}`}>{row[key]}</td>
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mt-3">
                      <small className="text-muted">
                        Visualizzando {totalRows === 0 ? 0 : pageStart + 1}-{Math.min(pageEnd, totalRows)} di {totalRows}
                      </small>
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={safeCurrentPage <= 1}
                          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        >
                          Precedente
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled>
                          Pagina {safeCurrentPage}/{totalPages}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={safeCurrentPage >= totalPages}
                          onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        >
                          Successiva
                        </button>
                      </div>
                    </div>
                    {safeCurrentPage === totalPages && totalRows > 0 && (
                      <div className="alert alert-light border mt-3 mb-0">
                        Fine risultati preview.
                      </div>
                    )}
                  </>
                )}

                {activeResultTab === 'regex' && (
                  <>
                    {regexTags.length === 0 ? (
                      <p className="text-muted mb-0">Nessuna regex disponibile per la preview corrente.</p>
                    ) : (
                      <>
                        <div className="d-flex justify-content-end mb-2">
                          <div className="custom-control custom-switch">
                            <input
                              id="show-combined-regex"
                              type="checkbox"
                              className="custom-control-input"
                              checked={showCombinedRegex}
                              onChange={(event) => setShowCombinedRegex(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="show-combined-regex">
                              Mostra regex combinata
                            </label>
                          </div>
                        </div>
                        <div
                          style={{
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            border: '1px solid #e9ecef',
                            borderRadius: '0.375rem',
                          }}
                        >
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>Tag</th>
                                {regexDimensionColumns.map((column) => (
                                  <th
                                    key={`regex-col-${column}`}
                                    style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}
                                  >
                                    {column}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {regexTags.map((tag) => (
                                <tr key={`regex-row-${tag}`}>
                                  <td>{tag}</td>
                                  {regexDimensionColumns.map((column) => {
                                    const cell = regexesByDimension?.[column]?.[tag] || {}
                                    const status = getRegexCellStatus(cell)
                                    const hasRegex = Boolean(cell?.pos_regex || cell?.neg_regex || cell?.combined_regex)
                                    return (
                                      <td key={`regex-cell-${column}-${tag}`}>
                                        <span className={`badge ${status.className}`}>{status.text}</span>
                                        {hasRegex && (
                                          <>
                                            <button
                                              type="button"
                                              className="btn btn-link btn-sm ml-2 p-0 align-baseline"
                                              onClick={() => setRegexDetail({ tag, column, cell })}
                                            >
                                              Dettagli
                                            </button>
                                            {showCombinedRegex && cell?.combined_regex && (
                                              <div className="mt-1">
                                                <small className="text-muted d-block">COMBINED</small>
                                                <code
                                                  style={{
                                                    display: 'block',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '360px',
                                                  }}
                                                  title={cell.combined_regex}
                                                >
                                                  {cell.combined_regex}
                                                </code>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </td>
                                    )
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}

                {activeResultTab === 'regex-compare' && (
                  <>
                    {regexTags.length === 0 ? (
                      <p className="text-muted mb-0">Nessuna regex disponibile per il confronto.</p>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted">
                            Confronto rispetto a <strong>{groundTruthColumn}</strong>
                          </small>
                          <div className="custom-control custom-switch">
                            <input
                              id="show-only-regex-differences"
                              type="checkbox"
                              className="custom-control-input"
                              checked={showOnlyRegexDifferences}
                              onChange={(event) => setShowOnlyRegexDifferences(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="show-only-regex-differences">
                              Solo differenze
                            </label>
                          </div>
                        </div>
                        <div
                          style={{
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            border: '1px solid #e9ecef',
                            borderRadius: '0.375rem',
                          }}
                        >
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>Tag</th>
                                <th style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                  {groundTruthColumn}
                                </th>
                                {regexModelColumns.map((column) => (
                                  <th
                                    key={`regex-compare-col-${column}`}
                                    style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}
                                  >
                                    {column}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {regexCompareTags.length === 0 && (
                                <tr>
                                  <td colSpan={2 + regexModelColumns.length}>Nessuna differenza trovata.</td>
                                </tr>
                              )}
                              {regexCompareTags.map((tag) => {
                                const groundTruthCell = regexesByDimension?.[groundTruthColumn]?.[tag] || {}
                                const groundTruthHasRegex = Boolean(
                                  groundTruthCell?.pos_regex || groundTruthCell?.neg_regex || groundTruthCell?.combined_regex
                                )
                                return (
                                  <tr key={`regex-compare-row-${tag}`}>
                                    <td>{tag}</td>
                                    <td>
                                      {groundTruthHasRegex ? (
                                        <span className="badge badge-primary">Ground truth</span>
                                      ) : (
                                        <span className="badge badge-light">Vuota</span>
                                      )}
                                    </td>
                                    {regexModelColumns.map((column) => {
                                      const modelCell = regexesByDimension?.[column]?.[tag] || {}
                                      const status = getComparisonStatus(groundTruthCell, modelCell)
                                      return (
                                        <td key={`regex-compare-cell-${column}-${tag}`}>
                                          <span className={`badge ${status.className}`}>{status.text}</span>
                                          {modelCell?.pos_regex || modelCell?.neg_regex || modelCell?.combined_regex ? (
                                            <button
                                              type="button"
                                              className="btn btn-link btn-sm ml-2 p-0 align-baseline"
                                              onClick={() =>
                                                setRegexCompareDetail({
                                                  tag,
                                                  column,
                                                  groundTruthColumn,
                                                  groundTruthCell,
                                                  modelCell,
                                                })
                                              }
                                            >
                                              Diff
                                            </button>
                                          ) : null}
                                        </td>
                                      )
                                    })}
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}

                {activeResultTab === 'warnings' && (
                  <>
                    <h5>Avvisi per modello</h5>
                    {Object.keys(warningsByModel).length > 0 ? (
                      Object.entries(warningsByModel).map(([model, warnings]) => (
                        <div key={model} className="mb-3">
                          <h6>{model}</h6>
                          {warnings && warnings.length > 0 ? (
                            <ul>
                              {warnings.map((warning, idx) => (
                                <li key={`${model}-warning-${idx}`}>{warning}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="mb-0">Nessun avviso</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">Nessun avviso modello disponibile.</p>
                    )}

                    <hr />
                    <h5>Avvisi ground truth</h5>
                    {groundTruthWarnings.length > 0 ? (
                      <ul>
                        {groundTruthWarnings.map((warning, idx) => (
                          <li key={`gt-warning-${idx}`}>{warning}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0">Nessun avviso ground truth.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {regexDetail && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Regex dettaglio: {regexDetail.tag} - {regexDetail.column}
                  </h5>
                  <button type="button" className="close" aria-label="Close" onClick={() => setRegexDetail(null)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>POS</strong>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => copyRegexText(regexDetail.cell?.pos_regex || '', 'POS')}
                      >
                        Copia POS
                      </button>
                    </div>
                    <pre
                      className="mt-2 mb-0 p-2 border rounded bg-light"
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {regexDetail.cell?.pos_regex || '-'}
                    </pre>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>NEG</strong>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => copyRegexText(regexDetail.cell?.neg_regex || '', 'NEG')}
                      >
                        Copia NEG
                      </button>
                    </div>
                    <pre
                      className="mt-2 mb-0 p-2 border rounded bg-light"
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {regexDetail.cell?.neg_regex || '-'}
                    </pre>
                  </div>

                  <div>
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Regex combinata (esecuzione)</strong>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => copyRegexText(regexDetail.cell?.combined_regex || '', 'regex combinata')}
                      >
                        Copia combinata
                      </button>
                    </div>
                    <pre
                      className="mt-2 mb-0 p-2 border rounded bg-light"
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {regexDetail.cell?.combined_regex || '-'}
                    </pre>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setRegexDetail(null)}>
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setRegexDetail(null)} />
        </>
      )}
      {regexCompareDetail && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Confronto regex: {regexCompareDetail.tag} - {regexCompareDetail.column}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={() => setRegexCompareDetail(null)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-12 col-lg-6">
                      <h6 className="mb-2">{regexCompareDetail.groundTruthColumn}</h6>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>POS</strong>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => copyRegexText(regexCompareDetail.groundTruthCell?.pos_regex || '', 'POS GT')}
                          >
                            Copia POS
                          </button>
                        </div>
                        <pre className="mt-2 mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {regexCompareDetail.groundTruthCell?.pos_regex || '-'}
                        </pre>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>NEG</strong>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => copyRegexText(regexCompareDetail.groundTruthCell?.neg_regex || '', 'NEG GT')}
                          >
                            Copia NEG
                          </button>
                        </div>
                        <pre className="mt-2 mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {regexCompareDetail.groundTruthCell?.neg_regex || '-'}
                        </pre>
                      </div>
                    </div>
                    <div className="col-12 col-lg-6">
                      <h6 className="mb-2">{regexCompareDetail.column}</h6>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>POS</strong>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => copyRegexText(regexCompareDetail.modelCell?.pos_regex || '', 'POS modello')}
                          >
                            Copia POS
                          </button>
                        </div>
                        <pre className="mt-2 mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {regexCompareDetail.modelCell?.pos_regex || '-'}
                        </pre>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>NEG</strong>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => copyRegexText(regexCompareDetail.modelCell?.neg_regex || '', 'NEG modello')}
                          >
                            Copia NEG
                          </button>
                        </div>
                        <pre className="mt-2 mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {regexCompareDetail.modelCell?.neg_regex || '-'}
                        </pre>
                      </div>
                    </div>
                  </div>
                  {showCombinedRegex && (
                    <div className="row mt-2">
                      <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                        <strong>COMBINED GT</strong>
                        <pre className="mt-2 mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {regexCompareDetail.groundTruthCell?.combined_regex || '-'}
                        </pre>
                      </div>
                      <div className="col-12 col-lg-6">
                        <strong>COMBINED MODELLO</strong>
                        <pre className="mt-2 mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {regexCompareDetail.modelCell?.combined_regex || '-'}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setRegexCompareDetail(null)}>
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setRegexCompareDetail(null)} />
        </>
      )}
    </>
  )
}

export { sortMetrics }
